import React, { Component } from 'react';
import styled from 'styled-components';
import Select from 'select-component';
import { Option } from 'react-select-v1';
import { colors } from 'bench-style-constants';

import JournalEntries, { type JournalEntry } from './JournalEntries';

import LedgerListContainer from './LedgerListContainer';
import MetaData from './MetaData';
import { linkifyText } from './linkify';

export const status = {
  BLANK: 'BLANK',
  NO_ERRORS: 'NO_ERRORS',
  MISSING_NOTES: 'MISSING_NOTES',
  ERRORS_FOUND: 'ERRORS_FOUND',
  INCOMPLETE: 'INCOMPLETE',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
};

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const Name = styled.span`
  font-size: 14px;
`;

const DescriptionContainer = styled.div`
  display: grid;
  grid-template-columns: 5fr 1fr;
  grid-gap: 8px;
  padding: 16px;
  background-color: ${colors.pearl};
  border: solid 1px ${colors.silt};
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.4;
`;

const Code = styled.div`
  padding-top: 4px;
  color: ${colors.dust};
  font-size: 12px;
`;

// Color is overwritten using important to
// override the defaults set by the angular app.
const StatusSelect = styled(Select)`
  .Select-value-label {
    text-transform: uppercase;
    color: ${({ value }) => {
      switch (value) {
        case status.NO_ERRORS:
          return colors.water;
        case status.MISSING_NOTES:
          return colors.gold;
        case status.ERRORS_FOUND:
          return colors.copper;
        case status.INCOMPLETE:
          return colors.smoke;
        default:
          return colors.charcoal;
      }
    }} !important;
  }
`;

const Description = styled.div`
  margin: auto 0;
  white-space: pre-line;
`;

const StatusOption = styled(Option)`
  text-transform: uppercase;
  color: ${({ option }) => {
    switch (option.value) {
      case status.NO_ERRORS:
        return colors.water;
      case status.MISSING_NOTES:
        return colors.gold;
      case status.ERRORS_FOUND:
        return colors.copper;
      case status.INCOMPLETE:
        return colors.smoke;
      default:
        return colors.charcoal;
    }
  }};
`;

const statusOptions = [
  {
    value: status.NO_ERRORS,
    label: 'No Errors',
  },
  {
    value: status.MISSING_NOTES,
    label: 'Missing Notes',
  },
  {
    value: status.ERRORS_FOUND,
    label: 'Errors Found',
  },
  {
    value: status.INCOMPLETE,
    label: 'Incomplete',
  },
];

export type ReviewCheckProps = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  clientId: string;
  description: string;
  ledgerId: string;
  code: string;
  value?: keyof typeof status | null;
  journalEntries: Array<JournalEntry>;
  applicableLedgerCodes: Array<string>;
  applicableLedgerIds: Array<string>;
  onStatusUpdate: (id: string, ledgerId: string, newStatus: keyof typeof status) => void;
  onSidebarToggle: (data: { journalEntryId?: string; ledgerId?: string }) => void;
};

type ReviewCheckState = {
  selectedOption: string;
};

class ReviewCheck extends Component<ReviewCheckProps, ReviewCheckState> {
  static defaultProps = {
    children: null,
  };

  constructor(props: ReviewCheckProps) {
    super(props);

    this.state = {
      selectedOption: props.value || '',
    };
  }

  onStatusChange = (selectedOption?: Record<string, keyof typeof status>) => {
    if (!selectedOption) return;
    const { id, ledgerId, onStatusUpdate } = this.props;

    this.setState({
      selectedOption: selectedOption.value,
    });
    onStatusUpdate(id, ledgerId, selectedOption.value);
  };

  render() {
    const {
      name,
      code,
      startDate,
      endDate,
      clientId,
      description,
      ledgerId,
      journalEntries,
      applicableLedgerCodes,
      applicableLedgerIds,
      onSidebarToggle,
    } = this.props;

    const { selectedOption } = this.state;

    return (
      <Wrapper>
        {name && <Name>{name}</Name>}
        <LedgerListContainer
          ledgerCodes={applicableLedgerCodes}
          ledgerIds={[...applicableLedgerIds, ...(ledgerId ? [ledgerId] : [])]}
          clientId={clientId}
          startDate={startDate}
          endDate={endDate}
          onSidebarToggle={onSidebarToggle}
        />
        <MetaData startDate={startDate} code={code} clientId={clientId} endDate={endDate} />
        {journalEntries && (
          <JournalEntries
            data={journalEntries}
            startDate={startDate}
            endDate={endDate}
            clientId={clientId}
            onSidebarToggle={onSidebarToggle}
          />
        )}
        <DescriptionContainer>
          <Description>
            {linkifyText(description)}
            <Code>{code}</Code>
          </Description>
          <StatusSelect
            value={selectedOption}
            options={statusOptions}
            onChange={this.onStatusChange}
            optionComponent={StatusOption}
            clearable={false}
          />
        </DescriptionContainer>
      </Wrapper>
    );
  }
}

export default ReviewCheck;
